/**
 * The presence avatar stack.
 *
 * ## Why the stack is capped and the overflow is counted
 *
 * `MAX_PRESENCE` on the server is 50. Rendering fifty overlapping avatars
 * produces a smear that communicates nothing, and the stack's job is "who else is
 * here" at a glance. Past four, the remaining people become a count with the full
 * list available on hover and to a screen reader.
 *
 * ## Why the same account in two tabs appears twice
 *
 * Presence is per connection, not per user - see `Presence` in
 * `packages/shared/src/domain.ts`. Two tabs are two cursors, so collapsing them
 * would make the cursor overlay show one cursor that teleports between two
 * positions. The stack is the honest representation: two entries, because there
 * are two.
 *
 * The accessible label reads the whole list rather than just the count, because
 * "3 people" is not what a screen reader user needs when the question is "is
 * Priya here".
 */

import type { ReactNode } from 'react';
import { otherPresence, viewerCount } from '../state/board-reducer.js';
import { useBoard } from '../state/BoardProvider.js';
import { Avatar } from './Avatar.js';

const VISIBLE = 4;

export function PresenceStack(): ReactNode {
  const { state } = useBoard();
  const others = otherPresence(state);
  const total = viewerCount(state);

  const visible = others.slice(0, VISIBLE);
  const overflow = others.length - visible.length;

  const label =
    total <= 1
      ? 'You are the only person on this board.'
      : `${total} people are viewing this board: ${others.map((presence) => presence.name).join(', ')}.`;

  return (
    <div className="flex items-center gap-2">
      <ul className="flex items-center" aria-label={label}>
        {visible.map((presence, index) => (
          <li key={presence.clientId} style={{ marginLeft: index === 0 ? 0 : -8 }} className="relative">
            <Avatar name={presence.name} colour={presence.colour} size={26} title={presence.name} />
          </li>
        ))}
        {overflow > 0 && (
          <li
            style={{ marginLeft: -8 }}
            className="relative inline-flex h-[26px] w-[26px] items-center justify-center rounded-full border border-border bg-sunken text-[11px] font-semibold text-ink-muted"
            title={others
              .slice(VISIBLE)
              .map((presence) => presence.name)
              .join(', ')}
          >
            +{overflow}
          </li>
        )}
      </ul>
      <span className="text-xs text-ink-subtle tabular-nums">{total === 1 ? 'Just you' : `${total} here`}</span>
    </div>
  );
}
